import { useGlobalStore } from '../../stores/globalStore';
import type { DashboardCensusSummary, DashboardMealServiceStatus } from '../../types/dashboard';
import './NowStrip.css';

/**
 * Where the facility is, right now.
 *
 * Nothing on the dashboard collapses, and neither do these tiles: a zero
 * shows as a zero. Everything in it is a number someone reads out loud
 * during a handover: which meal is running, how many people are in the
 * building, how many selections are in, which day of the cycle it is.
 *
 * Absorbs the Meal Service Status card outright and takes the headline numbers
 * off Census Summary — that card keeps the breakdown, which is the part worth
 * a full box. Showing 230 twice on one screen is the kind of duplication this
 * rewrite exists to remove.
 */

function Stat({
  value,
  label,
  hint,
  tone = 'plain',
}: {
  value: string | number;
  label: string;
  hint?: string;
  tone?: 'plain' | 'accent' | 'warn';
}) {
  // A tile: the value large and bold, what it is underneath, and a hint line
  // (serving time, census split, outstanding count, cycle name) under that.
  // Jeff (2026-10-22) asked for these back after the plain pass and the
  // one-line band had flattened them.
  return (
    <div className={`now-stat now-stat--${tone}`}>
      <div className="now-stat-value">{value}</div>
      <div className="now-stat-label">{label}</div>
      {hint && <div className="now-stat-hint">{hint}</div>}
    </div>
  );
}

/**
 * Splits a trailing qualifier off a mealtime name: `"Breakfast (Tomorrow)"`
 * becomes `["Breakfast", "Tomorrow"]`.
 *
 * The stat value is bold and large and ellipsises, so the server's
 * parenthetical rendered as "Breakfast (Tomorro..." and the truncation ate the
 * only word that carried any information. The qualifier belongs on the hint
 * line underneath, which is already showing the serving time, so nothing
 * needs abbreviating.
 */
function splitMealQualifier(name: string): [string, string | undefined] {
  const m = /^(.*?)\s*\(([^()]+)\)\s*$/.exec(name);
  return m ? [m[1], m[2]] : [name, undefined];
}

export default function NowStrip({
  meal,
  census,
}: {
  meal?: DashboardMealServiceStatus;
  census?: DashboardCensusSummary;
}) {
  const plural = useGlobalStore((s) => s.residentPluralLabel);

  const residents = census?.totalResidents ?? 0;
  const guests = census?.totalGuests ?? 0;
  const total = residents + guests;

  const smDone = meal?.selectiveMenusCompleted ?? 0;
  const smTotal = meal?.selectiveMenusTotal ?? 0;
  // Outstanding selections are the one number here that is also a warning:
  // a meal cannot be served correctly until they are in.
  const smOutstanding = Math.max(0, smTotal - smDone);

  const ticketsPending = meal?.trayTicketsPending ?? 0;

  // The server appends "(Tomorrow)" to the next mealtime when the day rolls
  // over (DashboardDAL.cs:767). Both tiles go through the same split so a
  // qualifier added to either one lands on the hint rather than the ellipsis.
  const [currentMealName, currentMealWhen] = splitMealQualifier(meal?.currentMealtime || '-');
  const [nextMealName, nextMealWhen] = splitMealQualifier(meal?.nextMealtime || '-');
  const withWhen = (when: string | undefined, time: string | undefined) =>
    [when, time].filter(Boolean).join(' · ') || undefined;

  return (
    <div className="now-strip" aria-label="Current status">
      <Stat
        value={currentMealName}
        label="Current meal"
        hint={withWhen(currentMealWhen, meal?.currentMealtimeTime)}
        tone="accent"
      />
      <Stat
        value={nextMealName}
        label="Next"
        hint={withWhen(nextMealWhen, meal?.nextMealtimeTime)}
      />
      <Stat
        value={total}
        label="Census"
        hint={guests > 0 ? `${residents} ${plural.toLowerCase()} · ${guests} guests` : undefined}
      />
      {/* Every tile stays put, zero or not: a tile that came and went with
          the numbers left people hunting for it. */}
      <Stat
        value={smTotal > 0 ? `${smDone}/${smTotal}` : 0}
        label="Selective menus"
        hint={smTotal === 0 ? 'none due this meal' : smOutstanding > 0 ? `${smOutstanding} outstanding` : 'all in'}
        tone={smOutstanding > 0 ? 'warn' : 'plain'}
      />
      <Stat
        value={ticketsPending}
        label="Tickets to print"
        tone={ticketsPending > 0 ? 'warn' : 'plain'}
      />
      <Stat
        value={meal?.menuCycleName ? `Day ${meal.menuCycleDay}` : '-'}
        label="Menu cycle"
        hint={meal?.menuCycleName || 'no active menu cycle'}
      />
    </div>
  );
}
